import { useEffect, useId, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DayPoint, Summary } from "../api";
import { compact, full, longDate, pct, shortDate } from "./format";
import { todayIst, weekdayIndex } from "./calendar";

/**
 * The window, day by day.
 *
 * **Small multiples, not one chart with several series.** Leads, attempts
 * and connect rate live on wildly different scales — tens against hundreds
 * of thousands against a fraction — and putting them on one plot would need
 * a second y-axis. A dual-axis chart lets the author decide which line looks
 * like it is "above" the other by choosing the scales, so it is never used
 * here. Stacked plots share an x-axis instead, each with one series, which
 * also means none of them needs a legend: the title names the series.
 *
 * **Every date is on the axis.** The server sends one point per date in the
 * window, zeros included, so a day nothing happened holds its slot rather
 * than the bars closing up around it. A zero is drawn as a stub on the
 * baseline so it reads as "measured, and nothing" rather than as a gap; a
 * rate with nothing to divide is `null` and draws no bar at all, because 0%
 * would read as a catastrophic day rather than a quiet one.
 *
 * The last day is drawn hatched when it is today. A partial day plotted
 * solid beside complete ones reads as a collapse in volume every single
 * afternoon, which is the fastest way to teach somebody to ignore a chart.
 */
export default function Trend({ days, totals }: { days: DayPoint[]; totals: Summary }) {
  const hatchId = useId().replace(/:/g, "");
  const ref = useRef<HTMLDivElement>(null);
  const width = useWidth(ref);

  if (days.length === 0) {
    return <div className="empty">Nothing recorded in this window.</div>;
  }

  const today = todayIst();
  // Once a bar gets narrower than a day label, the axis names each Monday
  // instead — every bar is still drawn, and a faint rule marks where each
  // week starts. Measured in pixels rather than days, so 28 days is a label
  // per bar on a laptop and a label per week on a phone.
  const dense = width > 0 && (width - 60) / days.length < 20;
  const mondays = days.filter((d) => weekdayIndex(d.metric_date) === 0).map((d) => d.metric_date);

  const common = { days, today, hatchId, dense, mondays };

  return (
    <div className="trends" ref={ref}>
      {/* The partial-day texture, defined once for every plot on the page.
          Texture rather than a lighter tint: a paler bar still reads as a
          real value, whereas a hatch reads as "not comparable" — and it
          survives greyscale printing and forced-colors mode. */}
      <svg width="0" height="0" aria-hidden style={{ position: "absolute" }}>
        <defs>
          <pattern
            id={hatchId}
            width="6"
            height="6"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect width="6" height="6" fill="var(--viz-series-1)" opacity="0.25" />
            <line x1="0" y1="0" x2="0" y2="6" stroke="var(--viz-series-1)" strokeWidth="3" />
          </pattern>
        </defs>
      </svg>

      <Plot
        {...common}
        title="Leads created"
        value={(d) => d.leads_created}
        format={full}
        axis={compact}
        headline={`${full(totals.leads_created)} in window`}
      />
      <Plot
        {...common}
        title="Attempts"
        value={(d) => d.attempts}
        format={full}
        axis={compact}
        headline={`${full(totals.attempts)} in window`}
      />
      <Plot
        {...common}
        title="Connected"
        value={(d) => d.connected}
        format={full}
        axis={compact}
        headline={`${full(totals.connected)} in window`}
      />
      <Plot
        {...common}
        title="Connect rate"
        value={(d) => d.connect_rate}
        format={(v) => pct(v)}
        axis={(v) => pct(v, 0)}
        headline={`${pct(totals.connect_rate)} over window`}
        emptyText="no attempts"
      />
    </div>
  );
}

/** The element's content width, kept current as the layout changes. */
function useWidth(ref: React.RefObject<HTMLDivElement | null>): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

interface PlotProps {
  title: string;
  headline: string;
  days: DayPoint[];
  value: (d: DayPoint) => number | null;
  /** Full precision, for the tooltip. */
  format: (v: number) => string;
  /** Short, for the y-axis ticks. */
  axis: (v: number) => string;
  /** What a null value means, said in the tooltip. */
  emptyText?: string;
  today: string;
  hatchId: string;
  dense: boolean;
  mondays: string[];
}

interface Row {
  date: string;
  v: number | null;
  partial: boolean;
}

function Plot({
  title,
  headline,
  days,
  value,
  format,
  axis,
  emptyText,
  today,
  hatchId,
  dense,
  mondays,
}: PlotProps) {
  const rows: Row[] = days.map((d) => ({
    date: d.metric_date,
    v: value(d),
    partial: d.metric_date === today,
  }));

  return (
    <figure className="plot">
      <figcaption>
        <span className="plot-title">{title}</span>
        <span className="plot-peak">{headline}</span>
      </figcaption>

      <ResponsiveContainer width="100%" height={dense ? 150 : 164}>
        <BarChart
          data={rows}
          margin={{ top: 8, right: 4, bottom: 0, left: 0 }}
          barCategoryGap={dense ? "12%" : "18%"}
          accessibilityLayer
        >
          {/* Recessive: the grid is orientation, not data. */}
          <CartesianGrid vertical={false} stroke="var(--line)" />
          {dense &&
            mondays.map((m) => (
              <ReferenceLine key={m} x={m} stroke="var(--line)" strokeDasharray="2 3" zIndex={-50} />
            ))}
          <XAxis
            dataKey="date"
            interval={0}
            ticks={dense ? mondays : undefined}
            tickLine={false}
            axisLine={{ stroke: "var(--line)" }}
            height={dense ? 22 : 34}
            tick={(p) => <DateTick {...p} dense={dense} />}
          />
          <YAxis
            width={52}
            tickCount={4}
            tickFormatter={axis}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: "var(--muted)" }}
          />
          <Tooltip
            cursor={{ fill: "var(--line)", opacity: 0.6 }}
            isAnimationActive={false}
            content={({ active, payload }) => {
              const row = active ? (payload?.[0]?.payload as Row | undefined) : undefined;
              if (!row) return null;
              return (
                <div className="plot-tip">
                  <div className="plot-tip-date">{longDate(row.date)}</div>
                  <div className="plot-tip-value">
                    {row.v === null ? `— ${emptyText ?? "no data"}` : format(row.v)}
                  </div>
                  {row.partial && <div className="plot-tip-note">Today, still in progress</div>}
                  {row.v === 0 && <div className="plot-tip-note">Nothing recorded</div>}
                </div>
              );
            }}
          />
          <Bar
            dataKey="v"
            isAnimationActive={false}
            shape={(p: unknown) => <BarMark {...(p as BarShape)} hatchId={hatchId} />}
          />
        </BarChart>
      </ResponsiveContainer>
    </figure>
  );
}

interface TickProps {
  x?: number | string;
  y?: number | string;
  payload?: { value: string };
  dense: boolean;
}

/**
 * A date under every bar: the day number, with the weekday beneath it so
 * a weekend dip explains itself. Mondays are set heavier so weeks can be
 * counted off the axis without reading it.
 */
function DateTick({ x, y, payload, dense }: TickProps) {
  if (!payload) return null;
  const iso = payload.value;
  const monday = weekdayIndex(iso) === 0;
  const day = Number(iso.slice(8, 10));
  if (dense) {
    return (
      <text x={x} y={Number(y) + 12} textAnchor="middle" className="plot-tick">
        {shortDate(iso)}
      </text>
    );
  }
  return (
    <g>
      <text
        x={x}
        y={Number(y) + 12}
        textAnchor="middle"
        className={monday ? "plot-tick monday" : "plot-tick"}
      >
        {/* The first of a month carries its name, so a window that crosses
            one says where. */}
        {day === 1 ? shortDate(iso) : day}
      </text>
      <text x={x} y={Number(y) + 25} textAnchor="middle" className="plot-tick weekday">
        {"MTWTFSS"[weekdayIndex(iso)]}
      </text>
    </g>
  );
}

interface BarShape {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: Row;
}

/**
 * One bar: rounded at the top only, hatched when it is today, and a thin
 * stub on the baseline when the value is zero.
 */
function BarMark({ x = 0, y = 0, width = 0, height = 0, payload, hatchId }: BarShape & { hatchId: string }) {
  if (!payload || payload.v === null) return null;
  const fill = payload.partial ? `url(#${hatchId})` : "var(--viz-series-1)";
  if (payload.v === 0) {
    // A zero bar is positioned on the baseline with no height.
    return <rect x={x} y={y + height - 2} width={width} height={2} fill="var(--muted)" opacity={0.6} />;
  }
  // Keep a sliver visible for a value that rounds to under a pixel, so a
  // quiet-but-nonzero day is distinguishable from a zero one.
  const h = Math.max(height, 1.5);
  return <path d={roundedTop(x, y + height - h, width, h)} fill={fill} />;
}

/**
 * A bar anchored to the baseline with only its top corners rounded.
 *
 * `rect rx` would round the bottom too, lifting the bar off its own
 * baseline and making small values look like they float.
 */
function roundedTop(x: number, y: number, w: number, h: number): string {
  const r = Math.min(4, w / 2, h);
  const bottom = y + h;
  return `M${x},${bottom} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${bottom} Z`;
}
